/**
 * BXAY Single Tab runtime — grid + Mini-PDP + bottom bar wiring.
 *
 * Phase 1: every product renders as a product card. Click opens the Mini-PDP
 * modal where the shopper picks every variant option, then "Add to Bundle"
 * appends a line. The sticky bottom bar reflects state via Spectrum.bxayCart.
 *
 * Phase 2 will add variant explosion: when a product has
 * `basisAttribute` set, the grid renders N variant cards (one per basis
 * value), and the Mini-PDP opens with that axis pre-selected.
 */
;(() => {
  const FEATURE_SLUG = 'bxay_single_tab'

  // Lucide "trash-2" — inline SVG (no Unicode glyph icons, per snippet rules).
  const TRASH_ICON =
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>'

  /** HTML-escape for innerHTML interpolations. Mirrors `sct9fpdr`'s helper —
   *  size labels etc. routinely contain `"` characters that break attribute
   *  context if injected raw. */
  function escapeHtml(s) {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  /** True for Shopify's placeholder label on a product with no real options.
   *  A single-variant product still carries one option/variant title, and
   *  Shopify names it "Default Title" — showing it tells the shopper nothing. */
  function isDefaultVariantLabel(value) {
    return String(value ?? '').trim().toLowerCase() === 'default title'
  }

  /**
   * Decompose a product into renderable cards.
   *
   * Phase 1 behaviour (no basis) → one product card per product.
   * Phase 2 (basis set)          → one card per value of the basis axis.
   *
   * When the basis covers the product's ONLY axis, the resulting variant
   * card is "fully resolved" — click adds directly (no Mini-PDP). When the
   * product has other axes (e.g. basis=Color but product also has Size),
   * each variant card carries `lockedOptions` that the Mini-PDP locks
   * down so the shopper only picks the remaining axes (R15).
   */
  function explodeProduct(product) {
    if (!product.basisAttribute) {
      return [
        { kind: 'product', product, lockedOptions: {}, basisValue: null, basisPosition: null },
      ]
    }
    const basis = product.options.find((o) => o.name === product.basisAttribute)
    if (!basis) {
      // Misconfigured — basis attribute name doesn't match any Shopify option.
      // Degrade gracefully to product-card mode.
      return [
        { kind: 'product', product, lockedOptions: {}, basisValue: null, basisPosition: null },
      ]
    }
    return basis.values.map((value) => ({
      kind: 'variant',
      product,
      lockedOptions: { [basis.position]: value },
      basisValue: value,
      basisPosition: basis.position,
    }))
  }

  /** Resolve the variant matching the locked selection, or null if no match. */
  function findVariantForLocked(product, lockedOptions, fillFirst) {
    for (const v of product.variants) {
      let match = true
      for (let pos = 1; pos <= 3; pos++) {
        const want = lockedOptions[pos]
        if (want != null && v[`option${pos}`] !== want) {
          match = false
          break
        }
      }
      if (match && (!fillFirst || v.available)) return v
    }
    return null
  }

  /** True when the basis covers every option axis the product has — click
   *  on the card resolves directly to a single variant without a modal. */
  function isFullyResolvedByBasis(product, basis) {
    if (!basis) return false
    const axes = product.options.length
    return axes === 1 && product.options[0].name === product.basisAttribute
  }

  /** True when at least one variant matches the locked selection AND is
   *  available — used to dim/mark OOS cards. */
  function lockedSelectionAvailable(product, lockedOptions) {
    for (const v of product.variants) {
      let match = true
      for (let pos = 1; pos <= 3; pos++) {
        const want = lockedOptions[pos]
        if (want != null && v[`option${pos}`] !== want) {
          match = false
          break
        }
      }
      if (match && v.available) return true
    }
    return false
  }

  function noopTrack() {}
  function safeTrack(track) {
    if (typeof track !== 'function') return noopTrack
    return (eventName, payload) => {
      try {
        track(eventName, payload)
      } catch (err) {
        console.warn(`[${FEATURE_SLUG}] analytics dispatch failed`, err)
      }
    }
  }

  /** Format a Shopify cent-amount as ₹X,XXX.00 (matches the theme's PLP price). */
  function rupee(cents) {
    if (cents == null) return ''
    const n = typeof cents === 'string' ? Number.parseFloat(cents) : cents
    if (Number.isNaN(n)) return ''
    return `₹${(n / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  /** Format a ms duration as HH:MM:SS (or MM:SS when <1h).
   *  Used by the urgency-banner countdown — must stay tabular-numeric. */
  function formatDuration(ms) {
    const totalSec = Math.max(0, Math.floor(ms / 1000))
    const h = Math.floor(totalSec / 3600)
    const m = Math.floor((totalSec % 3600) / 60)
    const s = totalSec % 60
    const pad = (n) => String(n).padStart(2, '0')
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`
  }

  class SaiBxay7n5pElement extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return
      this._mounted = true

      const tag = this.querySelector('script[data-spectrum-snippet-pool]')
      if (!tag) return
      let pool
      try {
        pool = JSON.parse(tag.textContent || '{}')
      } catch {
        return
      }
      if (!pool.config || !pool.products) return

      this._pool = pool
      this._config = pool.config
      this._products = pool.products
      this._props = pool.props || {}

      // Variant explosion is driven by the snippet-level `basisOptionIndex`
      // prop (option1/2/3), not a per-product metafield. Resolve the option
      // position to each product's option NAME up front so the name-based
      // explosion logic (explodeProduct etc.) applies uniformly. 'none' (or an
      // out-of-range position) leaves basisAttribute null → one card per product.
      const basisPos = { option1: 1, option2: 2, option3: 3 }[this._config.basisOptionIndex] ?? 0
      for (const product of this._products) {
        const opt = basisPos >= 1 ? (product.options || [])[basisPos - 1] : null
        product.basisAttribute = opt ? opt.name : null
      }

      this._track = noopTrack
      this._minipdpProductIdx = -1
      this._minipdpSelection = {}

      this._grid = this.querySelector('[data-spectrum-bxay-grid]')
      this._minipdp = this.querySelector('[data-spectrum-bxay-minipdp]')
      this._bar = this.querySelector('[data-spectrum-bxay-bar]')

      const snippetApi = window.__spectrumAi?.snippet
      if (snippetApi && typeof snippetApi.bind === 'function') {
        const handles = snippetApi.bind(this, () => {})
        if (handles?.track) this._track = safeTrack(handles.track)
      }

      const cart = window.Spectrum?.bxayCart
      if (!cart) {
        console.warn(`[${FEATURE_SLUG}] bxayCart engine not loaded`)
        return
      }
      cart.init(this._config)

      // Render the grid BEFORE subscribing: subscribe()'s immediate paint fires
      // with the cart still loading (ready === false) and disables the grid +
      // bar controls, so the cards must already exist for that gate to land on
      // them. The real cart paint re-enables them once getCart resolves.
      this._renderGrid()
      this._unsubscribeCart = cart.subscribe((state) => this._paint(state))
      this._bindEvents()
      this._startUrgencyTimer()
      this._track(`${FEATURE_SLUG}:page_view`, {
        config_handle: this._config.handle,
        collection_handle: this._config.collectionHandle,
        product_count: this._products.length,
        bundle_id: this._config.bundleSlug || '',
        entity_id: this._config.bundleSlug || '',
        entity_type: 'bundle',
      })
    }

    disconnectedCallback() {
      document.body.style.paddingBottom = ''
      if (this._unsubscribeCart) this._unsubscribeCart()
      if (this._urgencyInterval) {
        clearInterval(this._urgencyInterval)
        this._urgencyInterval = null
      }
      if (this._onKeyDown) {
        document.removeEventListener('keydown', this._onKeyDown)
        this._onKeyDown = null
      }
    }

    // ── Grid render ──

    _renderGrid() {
      if (!this._grid) return
      // Phase 2: build the flat card list by exploding any product with a
      // basis attribute. `_cards` is the source of truth for click handling.
      // `oosDisplay` modes:
      //   • `show_disabled` (default) — render dimmed + disabled, can't click.
      //   • `show_with_badge` — render at full opacity with a "Sold out"
      //     badge overlay, still disabled (Mini-PDP would have no buyable
      //     variant — block at the card boundary).
      //   • `hide` — drop the card from `_cards` entirely. Drops the index
      //     before paint so click handlers can't fire on a hidden card.
      const oosMode = this._config.oosDisplay ?? 'show_disabled'
      // A locked product is pre-added and cannot be removed, so it must not also
      // appear as a selectable card — otherwise the shopper sees an Add button
      // for something already in the bundle. It is pinned ahead of the grid as a
      // read-only card instead, so the pairing stays visible.
      const lockedHandle = this._config.fixedProductHandle || ''
      this._cards = []
      for (let i = 0; i < this._products.length; i++) {
        const product = this._products[i]
        if (lockedHandle && product.handle === lockedHandle) continue
        const cards = explodeProduct(product)
        for (const c of cards) {
          const oos = this._isCardOos(c)
          if (oos && oosMode === 'hide') continue
          this._cards.push({ ...c, productIdx: i })
        }
      }

      const html = []
      if (lockedHandle) html.push(this._lockedCardHtml(lockedHandle))
      for (let cardIdx = 0; cardIdx < this._cards.length; cardIdx++) {
        const c = this._cards[cardIdx]
        const p = c.product
        const isVariant = c.kind === 'variant'

        // Resolve the card's image + price + availability based on whether
        // it's a product card or a variant card.
        const variantHint = isVariant ? findVariantForLocked(p, c.lockedOptions, false) : null
        const image = (isVariant && variantHint?.image) || p.image
        const price = isVariant && variantHint ? variantHint.price : p.price
        const compareAtPrice =
          isVariant && variantHint ? variantHint.compareAtPrice : p.compareAtPrice
        const oos = this._isCardOos(c)
        const showCompare =
          this._props.showCompareAtPrice && compareAtPrice && compareAtPrice > price

        const ariaLabel = isVariant ? `${p.title} — ${c.basisValue ?? ''}` : p.title
        const subtitle = isVariant
          ? `<span class="spectrum-bundle-builder__card-subtitle">${escapeHtml(c.basisValue ?? '')}</span>`
          : ''
        const compareAtHtml = showCompare
          ? `<span class="spectrum-bundle-builder__card-compare-at">${rupee(compareAtPrice)}</span>`
          : ''
        const priceHtml = this._props.showPricePerCard
          ? `<div class="spectrum-bundle-builder__card-prices"><span class="spectrum-bundle-builder__card-price">${rupee(price)}</span>${compareAtHtml}</div>`
          : ''
        const oosBadge =
          oos && oosMode === 'show_with_badge'
            ? '<span class="spectrum-bundle-builder__card-oos-badge">Sold out</span>'
            : ''
        // The card is a container; the action slot holds an ADD button (default)
        // that `_paintCardStates` swaps for a quantity stepper once the product's
        // variant is in the bundle. The image affordance is config-driven:
        //   • cardImageAction 'minipdp' (default) → a button that opens the
        //     in-place Mini-PDP variant picker.
        //   • cardImageAction 'pdp' → a real anchor to the product page. As a
        //     link (not a cart action) it stays usable while the cart loads, and
        //     OOS products remain viewable — only the ADD button gates on stock.
        const addBtn = oos
          ? ''
          : `<button type="button" class="spectrum-bundle-builder__card-add" data-action="open-minipdp" data-card-idx="${cardIdx}">Add to cart</button>`
        const imageInner = `<span class="spectrum-bundle-builder__card-img" style="background-image: url('${escapeHtml(image ?? '')}')" aria-hidden="true"></span>${oosBadge}`
        const imageEl =
          this._config.cardImageAction === 'pdp'
            ? `<a class="spectrum-bundle-builder__card-imgbtn"
                    href="${escapeHtml(p.url || `/products/${p.handle}`)}"
                    aria-label="${escapeHtml(ariaLabel)}">${imageInner}</a>`
            : `<button type="button"
                    class="spectrum-bundle-builder__card-imgbtn"
                    data-action="open-minipdp"
                    data-card-idx="${cardIdx}"
                    ${oos ? 'disabled' : ''}
                    aria-label="${escapeHtml(ariaLabel)}">${imageInner}</button>`
        html.push(`
          <div class="spectrum-bundle-builder__card${oos ? ' is-oos' : ''}"
               data-card-idx="${cardIdx}"
               data-product-handle="${escapeHtml(p.handle)}">
            ${imageEl}
            <div class="spectrum-bundle-builder__card-body">
              <span class="spectrum-bundle-builder__card-title">${escapeHtml(p.title)}</span>
              ${subtitle}
              ${priceHtml}
              <div class="spectrum-bundle-builder__card-action" data-slot="action" data-card-idx="${cardIdx}">${addBtn}</div>
            </div>
          </div>
        `)
      }
      this._grid.innerHTML = html.join('')
    }

    /** Read-only card for the locked product. Carries no card index and no
     *  action button, so every click path (`open-minipdp`, `qty-inc`, …) keys
     *  off data this card does not have and cannot fire against it. Returns
     *  empty when the handle is absent from the collection feeding the grid —
     *  the missing card is the visible signal that the section's locked product
     *  and its source collection disagree. */
    _lockedCardHtml(handle) {
      const p = this._products.find((x) => x.handle === handle)
      if (!p) {
        console.warn(`[${FEATURE_SLUG}] locked product not in collection`, { handle })
        return ''
      }
      const label = this._config.fixedProductLabel || 'Included'
      const priceHtml = this._props.showPricePerCard
        ? `<div class="spectrum-bundle-builder__card-prices"><span class="spectrum-bundle-builder__card-price">${rupee(p.price)}</span></div>`
        : ''
      return `
        <div class="spectrum-bundle-builder__card is-locked"
             data-product-handle="${escapeHtml(p.handle)}">
          <span class="spectrum-bundle-builder__card-imgbtn">
            <span class="spectrum-bundle-builder__card-img" style="background-image: url('${escapeHtml(p.image ?? '')}')" aria-hidden="true"></span>
          </span>
          <div class="spectrum-bundle-builder__card-body">
            <span class="spectrum-bundle-builder__card-title">${escapeHtml(p.title)}</span>
            ${priceHtml}
            <div class="spectrum-bundle-builder__card-action">
              <span class="spectrum-bundle-builder__card-locked">${escapeHtml(label)}</span>
            </div>
          </div>
        </div>
      `
    }

    /** Shared OOS predicate — encapsulates the variant vs product split so
     *  filtering (oosDisplay === 'hide') and render-time `is-oos` use the
     *  exact same definition. */
    _isCardOos(c) {
      if (c.kind === 'variant') return !lockedSelectionAvailable(c.product, c.lockedOptions)
      return !c.product.available
    }

    // ── Mini-PDP ──

    /**
     * Open the Mini-PDP for a card. When called from a variant card, the
     * basis axis is pre-selected and rendered as locked (greyed swatch +
     * no click). When the card is "fully resolved by basis" (single-axis
     * product), we skip the Mini-PDP and add the variant directly.
     */
    async _openMinipdpForCard(cardIdx, btn) {
      const card = this._cards?.[cardIdx]
      if (!card) return
      const product = card.product

      // Single-variant product → nothing to choose; add it directly, never
      // open the Mini-PDP (a modal with one locked option is pure friction).
      if (Array.isArray(product.variants) && product.variants.length === 1) {
        const only = product.variants[0]
        if (only?.available) {
          await this._runWithLoader(btn, () => this._addVariantToBundle(product, only))
        }
        return
      }

      // Single-axis product with basis = that axis → R14, no modal.
      if (card.kind === 'variant') {
        const basis = product.options.find((o) => o.name === product.basisAttribute)
        if (basis && isFullyResolvedByBasis(product, basis)) {
          const variant = findVariantForLocked(product, card.lockedOptions, true)
          if (variant?.available) {
            await this._runWithLoader(btn, () => this._addVariantToBundle(product, variant))
          }
          return
        }
      }

      this._minipdpProductIdx = card.productIdx
      this._minipdpLockedOptions = card.lockedOptions
      this._minipdpSelection = {}
      // Locked axes start selected at their locked value; the rest default to
      // the first available value (which the shopper can change).
      for (const opt of product.options) {
        const locked = card.lockedOptions[opt.position]
        this._minipdpSelection[opt.position] = locked ?? opt.values[0]
      }
      this._renderMinipdpBody(product)
      this._minipdp.dataset.open = 'true'
      this._minipdp.setAttribute('aria-hidden', 'false')
      this._track(`${FEATURE_SLUG}:product_click`, {
        product_handle: product.handle,
        card_kind: card.kind,
        basis_value: card.basisValue,
        bundle_id: this._config.bundleSlug || '',
        entity_id: this._config.bundleSlug || '',
        entity_type: 'bundle',
      })
    }

    // Back-compat shim — older call sites referenced `_openMinipdp(idx)` with
    // a product index. New callers go through `_openMinipdpForCard(cardIdx)`.
    _openMinipdp(productIdx) {
      // Find the first card belonging to this product (its product card or
      // its first variant card).
      const cardIdx = (this._cards ?? []).findIndex((c) => c.productIdx === productIdx)
      if (cardIdx === -1) return
      this._openMinipdpForCard(cardIdx)
    }

    _closeMinipdp() {
      this._minipdp.dataset.open = 'false'
      this._minipdp.setAttribute('aria-hidden', 'true')
      this._minipdpProductIdx = -1
    }

    _renderMinipdpBody(product) {
      const titleEl = this._minipdp.querySelector('[data-slot="title"]')
      const bodyEl = this._minipdp.querySelector('[data-slot="body"]')
      if (titleEl) titleEl.textContent = product.title
      if (!bodyEl) return

      const locked = this._minipdpLockedOptions ?? {}
      const resolvedVariant = this._resolveMinipdpVariant(product)

      // Carousel: resolved-variant image first, then the product's gallery
      // (deduped), falling back to the featured image. One slide = one tile.
      const imgs = []
      const pushImg = (u) => {
        if (u && !imgs.includes(u)) imgs.push(u)
      }
      pushImg(resolvedVariant?.image)
      if (Array.isArray(product.images)) for (const u of product.images) pushImg(u)
      pushImg(product.image)
      const html = [
        `<div class="spectrum-bundle-builder__minipdp-carousel">${imgs
          .map(
            (u) =>
              `<span class="spectrum-bundle-builder__minipdp-slide" style="background-image: url('${escapeHtml(u)}')"></span>`,
          )
          .join('')}</div>`,
      ]

      for (const opt of product.options) {
        const lockedValue = locked[opt.position]
        const isLocked = lockedValue != null
        const selected = isLocked ? lockedValue : this._minipdpSelection[opt.position]
        html.push('<div class="spectrum-bundle-builder__minipdp-optgroup">')
        html.push(
          `<div class="spectrum-bundle-builder__minipdp-section-label">${escapeHtml(opt.name)}: <span class="spectrum-bundle-builder__minipdp-selected">${escapeHtml(selected ?? '')}</span></div>`,
        )
        html.push('<div class="spectrum-bundle-builder__minipdp-row">')
        for (const value of opt.values) {
          const isSel = selected === value
          const available = isLocked
            ? value === lockedValue
            : this._isOptionValueAvailableWithLock(product, opt.position, value, locked)
          const cls = `spectrum-bundle-builder__minipdp-pill${isSel ? ' is-selected' : ''}${available ? '' : ' is-oos'}`
          // Two-tone: split the option value verbatim, e.g. "S (2-5 UK)" →
          // main "S" + sub "(2-5 UK)". No paren → single label.
          const m = String(value).match(/^(.*?)\s*(\(.*\))\s*$/)
          const main = escapeHtml(m ? m[1] : value)
          const sub = m ? `<span class="spectrum-bundle-builder__minipdp-pill-sub">${escapeHtml(m[2])}</span>` : ''
          const attrs = isLocked
            ? 'aria-disabled="true" disabled'
            : available
              ? `data-action="pick-option" data-position="${opt.position}" data-value="${escapeHtml(value)}"`
              : 'disabled'
          html.push(
            `<button type="button" class="${cls}" ${attrs}><span class="spectrum-bundle-builder__minipdp-pill-main">${main}</span>${sub}</button>`,
          )
        }
        html.push('</div></div>')
      }

      const price = resolvedVariant?.price ?? product.price
      html.push(`<div class="spectrum-bundle-builder__minipdp-price">${rupee(price)}</div>`)
      bodyEl.innerHTML = html.join('')

      this._paintMinipdpCta(product, resolvedVariant)
    }

    /** Footer of the Mini-PDP: ADD TO CART, or a quantity stepper when this
     *  exact variant is already in the bundle (mirrors the card stepper). */
    _paintMinipdpCta(product, variant) {
      const slot = this._minipdp?.querySelector('[data-slot="minipdp-cta"]')
      if (!slot) return
      const cart = window.Spectrum?.bxayCart
      const vid = variant?.id
      const inCart = cart && vid != null ? cart.quantityForVariant(vid) : 0
      if (inCart > 0) {
        const v = escapeHtml(String(vid))
        const incDisabled =
          this._config.quantityRestriction === 'max_one_per_product' && inCart >= 1 ? ' disabled' : ''
        slot.innerHTML = `<div class="spectrum-bundle-builder__minipdp-stepper">
            <button type="button" class="spectrum-bundle-builder__stepper-btn" data-action="qty-dec" data-variant-id="${v}" aria-label="Decrease quantity">−</button>
            <span class="spectrum-bundle-builder__stepper-qty">${inCart}</span>
            <button type="button" class="spectrum-bundle-builder__stepper-btn" data-action="qty-inc" data-variant-id="${v}" aria-label="Increase quantity"${incDisabled}>+</button>
          </div>`
      } else {
        const available = !!variant?.available
        const capOk = !cart || cart.canAdd?.({ productHandle: product.handle }) !== false
        slot.innerHTML = `<button type="button" class="spectrum-bundle-builder__minipdp-cta" data-action="add-to-bundle" ${available && capOk ? '' : 'disabled'}>ADD TO CART</button>`
      }
    }

    /** Resolve the variant matching the current Mini-PDP selection. */
    _resolveMinipdpVariant(product) {
      for (const v of product.variants) {
        const o1 = this._minipdpSelection[1] ?? null
        const o2 = this._minipdpSelection[2] ?? null
        const o3 = this._minipdpSelection[3] ?? null
        if (
          (v.option1 ?? null) === o1 &&
          (v.option2 ?? null) === o2 &&
          (v.option3 ?? null) === o3
        ) {
          return v
        }
      }
      return null
    }

    /** Check whether any variant exists that has this option value, irrespective
     *  of the other axes (used to gate the swatch as OOS / unavailable). */
    _isOptionValueAvailable(product, position, value) {
      const key = `option${position}`
      return product.variants.some((v) => v[key] === value && v.available)
    }

    /** Locked-aware availability: a swatch is available when at least one
     *  variant matches BOTH the locked axes and this value at this position. */
    _isOptionValueAvailableWithLock(product, position, value, lockedOptions) {
      const key = `option${position}`
      return product.variants.some((v) => {
        if (!v.available) return false
        if (v[key] !== value) return false
        for (let pos = 1; pos <= 3; pos++) {
          const want = lockedOptions[pos]
          if (want != null && v[`option${pos}`] !== want) return false
        }
        return true
      })
    }

    // ── Bottom bar paint ──

    _paint(cartState) {
      if (!this._bar) return

      // Controls stay disabled until the authoritative cart has loaded. There is
      // no optimistic cache, so `ready === false` (the first getCart hasn't
      // resolved) means every state the UI could paint is provisional — acting
      // on it is never valid. Disable the grid + bar, skip the paint, and let
      // the real cart paint (and re-enable) once it lands.
      const loading = !cartState.ready
      const setButtonsDisabled = (root, disabled) => {
        if (!root) return
        for (const el of root.querySelectorAll('button')) {
          // On re-enable, leave out-of-stock card image buttons disabled: they
          // render `disabled` and `_paintCardStates` skips `.is-oos` cards, so
          // this gate is the only thing that could wrongly resurrect them. Every
          // other persistently-disabled control is re-derived by the paint below.
          if (!disabled && el.closest('.spectrum-bundle-builder__card.is-oos')) continue
          el.disabled = disabled
        }
      }
      setButtonsDisabled(this._grid, loading)
      setButtonsDisabled(this._bar, loading)
      // Skeleton runs for exactly the window the controls are disabled, so the
      // bar never sits visibly empty while the cart is still resolving.
      this._bar.classList.toggle('is-loading', loading)
      if (loading) return

      const target = this._config.quantityRequirement || cartState.tier?.quantity || 0
      const count = cartState.count
      const empty = count === 0
      const complete = !!cartState.tier?.isMet

      const offerEl = this._bar.querySelector('[data-slot="offer"]')
      const slotsEl = this._bar.querySelector('[data-slot="slots"]')
      const countEl = this._bar.querySelector('[data-slot="count"]')
      const totalEl = this._bar.querySelector('[data-slot="total"]')
      const expandedEl = this._bar.querySelector('[data-slot="expanded"]')
      const cta = this._bar.querySelector('[data-action="checkout"]')

      // Offer strip: black until the shopper starts → green once items are in;
      // flips to the savings/unlock copy when the bundle is complete.
      this._bar.classList.toggle('is-started', !empty)
      this._bar.classList.toggle('is-complete', complete)

      // The summary is a toggle for the editable item list — meaningless with
      // nothing in the bundle. Disable it while empty and collapse any open list.
      const summaryEl = this._bar.querySelector('[data-action="toggle-expand"]')
      if (summaryEl) summaryEl.disabled = empty
      if (empty && expandedEl && !expandedEl.hidden) {
        expandedEl.hidden = true
        this._bar.classList.remove('is-expanded')
        summaryEl?.setAttribute('aria-expanded', 'false')
      }
      if (offerEl) {
        if (complete && cartState.savings > 0) {
          const amt = Math.round(cartState.savings / 100).toLocaleString('en-IN')
          offerEl.innerHTML = `<strong class="spectrum-bundle-builder__bar-offer-strong">Offer Unlocked!</strong> You are saving ₹${amt}`
        } else if (!empty && target > count) {
          // Partial progress → nudge toward the discount threshold. Template is
          // merchant-configurable; {remaining} = items left to unlock the deal,
          // {count} = items chosen, {x}/{target} = the requirement.
          const remaining = target - count
          const tpl =
            this._config.progressNudgeTemplate ||
            'You are {remaining} away from getting your bundle discount'
          offerEl.textContent = tpl
            .replace(/\{remaining\}/g, String(remaining))
            .replace(/\{count\}/g, String(count))
            .replace(/\{x\}|\{target\}/g, String(target))
        } else {
          offerEl.textContent = this._bar.dataset.emptyCopy || ''
        }
      }

      // Slots: filled thumbnails (one per unit) then dashed empty slots up to
      // the requirement. Cap the row; overflow collapses to "+N".
      if (slotsEl) {
        const max = this._props.thumbnailMaxCount ?? 5
        const filled = cartState.lines.slice(0, max)
        const overflow = cartState.lines.length - filled.length
        let html = filled
          .map(
            (l) =>
              `<span class="spectrum-bundle-builder__bar-thumb" style="background-image: url('${escapeHtml(l.image ?? '')}')"></span>`,
          )
          .join('')
        if (overflow > 0) html += `<span class="spectrum-bundle-builder__bar-overflow">+${overflow}</span>`
        const emptySlots = Math.max(0, Math.min(target, max) - filled.length - (overflow > 0 ? 1 : 0))
        for (let i = 0; i < emptySlots; i++) {
          html += '<span class="spectrum-bundle-builder__bar-slot-empty" aria-hidden="true"></span>'
        }
        slotsEl.innerHTML = html
      }

      if (countEl) countEl.textContent = `${count} / ${target} Products`

      if (totalEl) {
        const mrp =
          cartState.retailTotal > cartState.total
            ? `<span class="spectrum-bundle-builder__bar-total-mrp">${rupee(cartState.retailTotal)}</span>`
            : ''
        totalEl.innerHTML = `<span class="spectrum-bundle-builder__bar-total-label">Total:</span> <span class="spectrum-bundle-builder__bar-total-amount">${rupee(cartState.total)}</span> ${mrp}`
      }

      if (cta) {
        cta.disabled = !cartState.canCheckout
        cta.textContent = this._config.checkoutCtaLabel ?? 'Add Bundle to Cart'
      }

      // Keep the expanded editable list in sync while it's open.
      if (expandedEl && !expandedEl.hidden) this._paintExpanded(cartState, expandedEl)

      // Reserve the fixed bar's height at the END OF THE PAGE. Putting it on
      // this section instead would push every following section down by that
      // amount, showing as a gap in the middle of the page rather than clearance
      // at the bottom. Cleared on disconnect so it can't outlive the section.
      const barHeight = this._bar.offsetHeight
      document.body.style.paddingBottom = `${barHeight + 16}px`

      // Repaint card "is-added" states.
      this._paintCardStates(cartState)

      // Keep the Mini-PDP footer (ADD ↔ stepper) in sync while it's open.
      if (this._minipdp?.dataset.open === 'true' && this._minipdpProductIdx >= 0) {
        const p = this._products[this._minipdpProductIdx]
        if (p) this._paintMinipdpCta(p, this._resolveMinipdpVariant(p))
      }
    }

    _paintCardStates(cartState) {
      if (!this._grid) return
      const groups = cartState.groups || []
      // Bundle is full once the picked count reaches the requirement — no more
      // units can be added (new products OR another of an existing one).
      const cap = this._config.quantityRequirement || Number.POSITIVE_INFINITY
      const full = cartState.count >= cap
      for (const card of this._grid.querySelectorAll('.spectrum-bundle-builder__card')) {
        if (card.classList.contains('is-oos')) continue
        const handle = card.dataset.productHandle
        const slot = card.querySelector('[data-slot="action"]')
        if (!slot) continue
        const productGroups = groups.filter((g) => g.productHandle === handle)
        const qty = productGroups.reduce((sum, g) => sum + g.quantity, 0)
        card.classList.toggle('is-added', qty > 0)
        if (qty > 0) {
          // Stepper targets the product's added variant (first group — the
          // one-variant-per-product common case).
          const vid = escapeHtml(String(productGroups[0].variantId))
          const incDisabled =
            full || (this._config.quantityRestriction === 'max_one_per_product' && qty >= 1)
              ? ' disabled'
              : ''
          slot.innerHTML = `<div class="spectrum-bundle-builder__stepper">
              <button type="button" class="spectrum-bundle-builder__stepper-btn" data-action="qty-dec" data-variant-id="${vid}" aria-label="Decrease quantity">−</button>
              <span class="spectrum-bundle-builder__stepper-qty">${qty}</span>
              <button type="button" class="spectrum-bundle-builder__stepper-btn" data-action="qty-inc" data-variant-id="${vid}" aria-label="Increase quantity"${incDisabled}>+</button>
            </div>`
        } else {
          const cardIdx = escapeHtml(String(slot.dataset.cardIdx))
          // Bundle full → new products can't be added.
          const addDisabled = full ? ' disabled' : ''
          slot.innerHTML = `<button type="button" class="spectrum-bundle-builder__card-add" data-action="open-minipdp" data-card-idx="${cardIdx}"${addDisabled}>Add to cart</button>`
        }
      }
    }

    /** Expand / collapse the bottom-bar selected-items list. */
    _toggleExpand() {
      const expandedEl = this._bar?.querySelector('[data-slot="expanded"]')
      const summary = this._bar?.querySelector('[data-action="toggle-expand"]')
      if (!expandedEl) return
      const cart = window.Spectrum?.bxayCart
      // Nothing to expand until at least one item is in the bundle.
      if (cart && cart.getState().count === 0) return
      const open = expandedEl.hidden
      expandedEl.hidden = !open
      this._bar.classList.toggle('is-expanded', open)
      summary?.setAttribute('aria-expanded', String(open))
      if (open && cart) this._paintExpanded(cart.getState(), expandedEl)
    }

    /** Per-product editable rows: thumbnail, title, unit price ×N + variant
     *  options, and a trash button (removes the variant via setQuantity 0). */
    _paintExpanded(cartState, el) {
      const groups = cartState.groups || []
      if (!groups.length) {
        el.innerHTML = `<div class="spectrum-bundle-builder__bar-row-empty">${escapeHtml(this._bar.dataset.emptyCopy || '')}</div>`
        return
      }
      el.innerHTML = groups
        .map((g) => {
          // Shopify gives a single-variant product one synthetic option named
          // "Title" with the value "Default Title". It carries no information
          // for the shopper, so drop it rather than print "Title: Default
          // Title" — real options and real variant titles still show.
          const realOpts = (g.options || []).filter((o) => !isDefaultVariantLabel(o.value))
          const opts =
            realOpts.map((o) => `${escapeHtml(o.name)}: ${escapeHtml(o.value)}`).join('  |  ') ||
            (isDefaultVariantLabel(g.variantTitle) ? '' : escapeHtml(g.variantTitle || ''))
          const vid = escapeHtml(String(g.variantId))
          // The locked line has no remove control: the engine refuses the call
          // anyway, so offering the button would only produce a dead click.
          const locked =
            this._config.fixedVariantId &&
            String(g.variantId) === String(this._config.fixedVariantId)
          const removeEl = locked
            ? `<span class="spectrum-bundle-builder__bar-row-locked">${escapeHtml(this._config.fixedProductLabel || 'Included')}</span>`
            : `<button type="button" class="spectrum-bundle-builder__bar-row-remove" data-action="remove-variant" data-variant-id="${vid}" aria-label="Remove item">${TRASH_ICON}</button>`
          return `<div class="spectrum-bundle-builder__bar-row${locked ? ' is-locked' : ''}">
              <span class="spectrum-bundle-builder__bar-row-thumb" style="background-image: url('${escapeHtml(g.image ?? '')}')"></span>
              <div class="spectrum-bundle-builder__bar-row-info">
                <span class="spectrum-bundle-builder__bar-row-title">${escapeHtml(g.displayLabel ?? '')}</span>
                <span class="spectrum-bundle-builder__bar-row-meta">${rupee(g.price)} &times;${g.quantity}${opts ? `<span class="spectrum-bundle-builder__bar-row-opts">${opts}</span>` : ''}</span>
              </div>
              ${removeEl}
            </div>`
        })
        .join('')
    }

    // ── Event wiring ──

    _bindEvents() {
      this.addEventListener('click', (e) => {
        const target = e.target.closest('[data-action]')
        if (!target) return
        const action = target.dataset.action
        if (action === 'open-minipdp') {
          e.preventDefault()
          if (target.disabled) return
          const cardIdx = Number.parseInt(target.dataset.cardIdx, 10)
          if (Number.isFinite(cardIdx)) this._openMinipdpForCard(cardIdx, target)
        } else if (action === 'close-minipdp') {
          e.preventDefault()
          this._closeMinipdp()
        } else if (action === 'pick-option') {
          e.preventDefault()
          if (target.classList.contains('is-oos')) return
          const pos = Number.parseInt(target.dataset.position, 10)
          const value = target.dataset.value
          if (Number.isFinite(pos) && value != null) {
            this._minipdpSelection[pos] = value
            const product = this._products[this._minipdpProductIdx]
            if (product) this._renderMinipdpBody(product)
          }
        } else if (action === 'add-to-bundle') {
          e.preventDefault()
          this._runWithLoader(target, () => this._addCurrentMinipdpToBundle())
        } else if (action === 'qty-inc' || action === 'qty-dec') {
          e.preventDefault()
          if (target.disabled) return
          const vid = target.dataset.variantId
          const cart = window.Spectrum?.bxayCart
          if (vid == null || !cart) return
          const current = cart.quantityForVariant(vid)
          if (action === 'qty-inc') {
            const cap = this._config.quantityRequirement || Number.POSITIVE_INFINITY
            // Bundle full → no more units. Max-one → never above 1 per product.
            if (cart.getState().count >= cap) return
            if (this._config.quantityRestriction === 'max_one_per_product' && current >= 1) return
          }
          const next = action === 'qty-inc' ? current + 1 : current - 1
          // Spinner on the +/- while the server round-trip is in flight.
          this._runWithLoader(target, () => cart.setQuantity(vid, next))
        } else if (action === 'remove-variant') {
          e.preventDefault()
          const vid = target.dataset.variantId
          const cart = window.Spectrum?.bxayCart
          // Spinner on the trash button while the removal round-trips. The row
          // re-renders away on success, so the finally-restore is a no-op then.
          if (vid != null && cart) this._runWithLoader(target, () => cart.setQuantity(vid, 0))
        } else if (action === 'toggle-expand') {
          e.preventDefault()
          this._toggleExpand()
        } else if (action === 'checkout') {
          e.preventDefault()
          this._runWithLoader(target, () => this._checkout())
        }
      })

      // Stored on `this` so `disconnectedCallback` can remove it. Shopify's
      // theme editor + Studio preview can re-mount sections, which would
      // otherwise leak one listener (and a `this` closure) per remount.
      this._onKeyDown = (e) => {
        if (e.key === 'Escape' && this._minipdp?.dataset.open === 'true') {
          e.preventDefault()
          this._closeMinipdp()
        }
      }
      document.addEventListener('keydown', this._onKeyDown)
    }

    /** Run an async cart action while a spinner shows on the button that
     *  triggered it, so a slow network round-trip is never a dead click. */
    async _runWithLoader(btn, fn) {
      if (btn) {
        btn.classList.add('is-loading')
        btn.setAttribute('aria-busy', 'true')
        btn.disabled = true
      }
      try {
        return await fn()
      } finally {
        if (btn && btn.isConnected) {
          btn.classList.remove('is-loading')
          btn.removeAttribute('aria-busy')
          btn.disabled = false
        }
      }
    }

    async _addCurrentMinipdpToBundle() {
      const product = this._products[this._minipdpProductIdx]
      if (!product) return
      const variant = this._resolveMinipdpVariant(product)
      if (!variant?.available) return
      // Stay open — the footer flips ADD → stepper once the add lands (via the
      // cart subscription → _paint → _paintMinipdpCta).
      await this._addVariantToBundle(product, variant)
    }

    /** Push a (product, variant) tuple into the bundle cart. Used by both
     *  the Mini-PDP "Add to Bundle" CTA AND the fully-resolved variant-card
     *  direct-add path. The cart engine talks to the backend, so the add is
     *  async; returns a promise resolving to whether it succeeded. */
    async _addVariantToBundle(product, variant) {
      const line = {
        productHandle: product.handle,
        variantId: variant.id,
        displayLabel: product.title,
        image: variant.image ?? product.image,
        price: typeof variant.price === 'string' ? Number.parseFloat(variant.price) : variant.price,
        compareAtPrice:
          variant.compareAtPrice == null
            ? null
            : typeof variant.compareAtPrice === 'string'
              ? Number.parseFloat(variant.compareAtPrice)
              : variant.compareAtPrice,
      }
      const cart = window.Spectrum?.bxayCart
      if (!cart) return false
      const ok = await cart.add(line)
      // No product_id: the SSR pool serializes products by handle only (no
      // Shopify product id field), so the handle is the product identifier.
      this._track(`${FEATURE_SLUG}:product_added`, {
        product_handle: product.handle,
        variant_id: variant.id,
        success: ok,
        bundle_id: this._config.bundleSlug || '',
        entity_id: this._config.bundleSlug || '',
        entity_type: 'bundle',
      })
      return ok
    }

    /** Bind the SSR'd urgency banner to a 1s tick.
     *
     *  The banner is server-rendered with `data-deadline` (ISO string) and
     *  `data-template` (`"Ends in {time}"`). We start the interval, format
     *  remaining time as MM:SS or HH:MM:SS, and when the deadline passes
     *  we remove the banner outright — leaving an "expired" UI looks worse
     *  than no UI. Multiple wrappers on a page aren't a thing for BXAY
     *  pages, but if the snippet element is reconnected we clear the
     *  previous interval before starting again (defensive). */
    _startUrgencyTimer() {
      const banner = this.querySelector('[data-spectrum-bxay-urgency]')
      if (!banner) return
      if (this._urgencyInterval) clearInterval(this._urgencyInterval)

      const deadlineMs = Date.parse(banner.dataset.deadline ?? '')
      const template = banner.dataset.template || 'Ends in {time}'
      const slot = banner.querySelector('[data-slot="copy"]')
      if (!Number.isFinite(deadlineMs) || !slot) {
        // No usable deadline → don't even render the banner.
        banner.remove()
        return
      }

      const tick = () => {
        const remaining = deadlineMs - Date.now()
        if (remaining <= 0) {
          clearInterval(this._urgencyInterval)
          this._urgencyInterval = null
          banner.remove()
          this._track(`${FEATURE_SLUG}:urgency_timer_expired`, {
            bundle_id: this._config.bundleSlug || '',
            entity_id: this._config.bundleSlug || '',
            entity_type: 'bundle',
          })
          return
        }
        slot.innerHTML = template.replace(
          '{time}',
          `<span class="spectrum-bundle-builder__urgency-time">${formatDuration(remaining)}</span>`,
        )
      }
      tick()
      this._urgencyInterval = setInterval(tick, 1000)
    }

    async _checkout() {
      const cart = window.Spectrum?.bxayCart
      if (!cart) return
      const state = cart.getState()
      // Line-level selection at checkout intent. The bundle cart identifies
      // items by variant + handle only (no Shopify product id on the backend
      // cart items), so lines carry variant_id + quantity.
      this._track(`${FEATURE_SLUG}:checkout_clicked`, {
        count: state.count,
        tier_met: state.tier?.isMet,
        total_cents: state.total,
        bundle_id: this._config.bundleSlug || '',
        entity_id: this._config.bundleSlug || '',
        entity_type: 'bundle',
        lines: (state.groups || []).map((g) => ({
          variant_id: g.variantId || '',
          quantity: g.quantity || 0,
        })),
      })
      if (!state.tier?.isMet && this._config.partialCheckoutAllowed) {
        const ok = window.confirm(
          this._config.partialWarningCopy ||
            'You haven’t reached the bundle deal. Continue anyway?',
        )
        if (!ok) return
      }
      // The backend bundle cart holds the items with the fixed-price discount
      // already applied; checkout() re-fetches and redirects to the server
      // checkout URL.
      // GoKwik checkout can fail at several points (cart snapshot, theme-cart
      // sync, discount apply, SDK absent) and each one restores the shopper's
      // cart and returns instead of opening. Silence would look like a dead
      // button, so surface it — the console carries the specific reason.
      const res = await cart.checkout()
      if (res && res.ok === false) {
        window.alert(
          this._config.checkoutErrorCopy ||
            'We couldn’t start checkout just now. Please try again.',
        )
      }
    }
  }

  // Test-harness shim — exposes the pure helpers so the explosion logic can
  // be exercised without mounting the custom element. Gated on a sentinel
  // that production never sets.
  if (typeof __SAI_BXAY_TEST__ !== 'undefined') {
    /* istanbul ignore next */
    globalThis.__spectrum_bundle_builder_api__ = {
      explodeProduct,
      findVariantForLocked,
      isFullyResolvedByBasis,
      lockedSelectionAvailable,
      escapeHtml,
      formatDuration,
    }
  }

  if (!customElements.get('spectrum-bundle-builder')) {
    customElements.define('spectrum-bundle-builder', SaiBxay7n5pElement)
  }
})()
